import React, { useState, useEffect, useCallback, useMemo, memo } from 'react';
import {
  Table,
  Button,
  Space,
  Modal,
  Form,
  Input,
  Card,
  Typography,
  message,
  Popconfirm,
  Tag,
  Switch,
  Row,
  Col,
  Statistic,
  Descriptions,
  Tabs,
  Select,
  Avatar,
  Tooltip,
  Badge,
  DatePicker,
  Empty,
  Alert,
  Spin
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  BankOutlined,
  UserOutlined,
  PhoneOutlined,
  MailOutlined,
  EnvironmentOutlined,
  KeyOutlined,
  ReloadOutlined,
  EyeInvisibleOutlined,
  TeamOutlined,
  UserAddOutlined,
  LockOutlined,
  SearchOutlined,
  ClearOutlined,
  ExclamationCircleOutlined
} from '@ant-design/icons';
import { useResponsive } from '../hooks/useResponsive';
import { useSimpleStore } from '../stores/simpleStore';
import { PageLayout } from './common';
import { getApiUrl } from '../config';

const { Title, Text } = Typography;
const { TextArea } = Input;

interface Company {
  id: number;
  name: string;
  code: string;
  business_number?: string;
  address?: string;
  phone?: string;
  email?: string;
  contact_person?: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  user_count?: number;
  route_count?: number;
  device_count?: number;
}

interface CompanyUser {
  id: number;
  username: string;
  email: string;
  name: string;
  role: 'admin' | 'operator';
  company_id: number;
  company_name?: string;
  is_active: boolean;
  last_login?: string;
  created_at: string;
  updated_at: string;
  password_hash?: string;
  phone?: string;
  position?: string;
}

const CompanyManagement: React.FC = memo(() => {
  const [companies, setCompanies] = useState<Company[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingCompany, setEditingCompany] = useState<Company | null>(null);
  const [form] = Form.useForm();
  const { user } = useSimpleStore();
  const { isMobile, isTablet } = useResponsive();
  
  // 운영자 관리 상태
  const [activeTab, setActiveTab] = useState('companies');
  const [selectedCompanyId, setSelectedCompanyId] = useState<number | null>(null);
  const [companyUsers, setCompanyUsers] = useState<CompanyUser[]>([]);
  const [userModalVisible, setUserModalVisible] = useState(false);
  const [editingUser, setEditingUser] = useState<CompanyUser | null>(null);
  const [userForm] = Form.useForm();
  const [userLoading, setUserLoading] = useState(false);

  // 통계 데이터
  const [stats, setStats] = useState({
    totalCompanies: 0,
    activeCompanies: 0,
    totalUsers: 0,
    totalRoutes: 0
  });

  // 검색 기능
  const [searchTerm, setSearchTerm] = useState('');

  // 디바운싱된 검색어
  const debouncedSearchTerm = useMemo(() => {
    const timer = setTimeout(() => searchTerm, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // 필터링된 운수사 목록
  const filteredCompanies = useMemo(() => {
    if (!searchTerm) return companies;
    return companies.filter(company =>
      company.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      company.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
      company.contact_person?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [companies, searchTerm]);

  // 필터링된 사용자 목록 - 배열 타입 가드 추가
  const filteredUsers = useMemo(() => {
    // 배열이 아닌 경우 빈 배열 반환
    if (!Array.isArray(companyUsers)) return [];
    if (!searchTerm) return companyUsers;
    return companyUsers.filter(user =>
      user?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user?.username?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user?.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user?.position?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [companyUsers, searchTerm]);

  // 회사 옵션 목록 - 메모이제이션
  const companySelectOptions = useMemo(() =>
    companies.map(company => ({
      value: company.id,
      label: `${company.name} (${company.code})`
    })), [companies]
  );

  useEffect(() => {
    if (user?.role === 'admin') {
      loadCompanies();
      loadStats();
    }
  }, [user]);

  // companies 데이터가 변경될 때마다 통계 업데이트
  useEffect(() => {
    if (companies.length > 0) {
      calculateStatsFromCompanies();
    }
  }, [companies]);

  const loadCompanies = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(getApiUrl('/api/companies'), {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          // 서버를 단일 진실 공급원(source of truth)으로 사용 — localStorage 목업 미사용
          setCompanies(data.data || []);
        } else {
          message.error(data.error || data.message || '운수사 목록을 불러올 수 없습니다.');
        }
      } else if (response.status === 401) {
        message.error('인증이 만료되었습니다. 다시 로그인해주세요.');
      } else {
        const errorData = await response.json().catch(() => ({}));
        message.error(errorData.error || errorData.message || '운수사 목록 로드 중 오류가 발생했습니다.');
      }
    } catch (error) {
      console.error('운수사 목록 로드 실패:', error);
      message.error('네트워크 연결을 확인해주세요.');
    } finally {
      setLoading(false);
    }
  };

  // companies 데이터를 기반으로 통계 계산
  const calculateStatsFromCompanies = useCallback(() => {
    const totalCompanies = companies.length;
    const activeCompanies = companies.filter(company => company.is_active).length;
    const totalUsers = companies.reduce((sum, company) => sum + (company.user_count || 0), 0);
    const totalRoutes = companies.reduce((sum, company) => sum + (company.route_count || 0), 0);

    setStats({
      totalCompanies,
      activeCompanies,
      totalUsers,
      totalRoutes
    });
  }, [companies]);

  const loadStats = useCallback(async () => {
    try {
      const token = localStorage.getItem('adminToken');
      if (!token) return;

      const response = await fetch(getApiUrl('/api/companies/stats'), {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setStats(data.data);
          return;
        }
      }
    } catch (error) {
      console.error('통계 데이터 로드 실패:', error);
    }

    // API가 실패하거나 없는 경우 현재 companies 데이터를 기반으로 통계 계산
    calculateStatsFromCompanies();
  }, [calculateStatsFromCompanies]);

  // 운영자 관련 함수들 - useCallback으로 최적화
  const loadCompanyUsers = useCallback(async () => {
    if (!selectedCompanyId) return;

    setUserLoading(true);
    try {
      const token = localStorage.getItem('adminToken');
      if (!token) {
        message.error('인증 토큰이 없습니다. 다시 로그인해주세요.');
        return;
      }

      const response = await fetch(getApiUrl(`/api/companies/${selectedCompanyId}/users`), {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          // 응답 데이터에서 users 배열 추출
          const userData = data.data?.users || data.data;
          setCompanyUsers(Array.isArray(userData) ? userData : []);
          console.log('✅ 운영자 목록 로드 성공:', userData); // 디버그용
        } else {
          message.error(data.message || '사용자 목록을 불러올 수 없습니다.');
          setCompanyUsers([]);
        }
      } else if (response.status === 401) {
        message.error('인증이 만료되었습니다. 다시 로그인해주세요.');
        setCompanyUsers([]);
      } else if (response.status === 404) {
        // 404 에러 시 빈 배열로 초기화 (새로운 운수사일 가능성)
        setCompanyUsers([]);
        message.info('아직 등록된 운영자가 없습니다.');
      } else {
        const errorData = await response.json().catch(() => ({}));
        message.error(errorData.message || '운영자 목록 로드 중 오류가 발생했습니다.');
        setCompanyUsers([]);
      }
    } catch (error) {
      console.error('운영자 목록 로드 실패:', error);
      if (error instanceof TypeError) {
        message.error('네트워크 연결을 확인해주세요.');
      } else {
        message.error('운영자 목록 로드 중 오류가 발생했습니다.');
      }
      setCompanyUsers([]);
    } finally {
      setUserLoading(false);
    }
  }, [selectedCompanyId]);

  // 목업 데이터 제거 - 실제 API만 사용

  const handleAddUser = useCallback(() => {
    if (!selectedCompanyId) {
      message.warning('운수사를 먼저 선택해주세요.');
      return;
    }
    setEditingUser(null);
    userForm.resetFields();
    userForm.setFieldsValue({
      role: 'operator',
      is_active: true,
      password: '' // 새 사용자는 비밀번호 필수
    });
    setUserModalVisible(true);
  }, [selectedCompanyId, userForm]);

  const handleEditUser = useCallback((user: CompanyUser) => {
    setEditingUser(user);
    userForm.setFieldsValue({
      username: user.username,
      name: user.name,
      email: user.email,
      role: user.role,
      phone: user.phone,
      position: user.position,
      is_active: user.is_active
    });
    setUserModalVisible(true);
  }, [userForm]);

  const handleUserSubmit = useCallback(async (values: any) => {
    if (!selectedCompanyId) {
      message.error('운수사를 먼저 선택해주세요.');
      return;
    }

    setUserLoading(true);
    try {
      // 새 사용자 생성 시 password 필드 필수 검증
      if (!editingUser && !values.password) {
        message.error('비밀번호를 입력해주세요.');
        setUserLoading(false);
        return;
      }

      // payload에 필수 필드 모두 포함
      const payload = {
        username: values.username,
        email: values.email,
        name: values.name,
        role: values.role,
        phone: values.phone || null,
        position: values.position || null,
        company_id: selectedCompanyId,
        is_active: values.is_active !== undefined ? values.is_active : true
      };

      // 새 사용자 생성 시에만 password 추가
      if (!editingUser && values.password) {
        payload.password = values.password;
      }

      // 환경변수 확인 로그
      console.log('🌍 Environment check:', {
        VITE_API_BASE_URL: import.meta.env.VITE_API_BASE_URL,
        getApiUrl_test: getApiUrl('/api/users'),
        mode: import.meta.env.MODE
      });

      console.log('📤 Sending user payload:', payload); // 디버그용

      const token = localStorage.getItem('adminToken');
      if (!token) {
        message.error('인증 토큰이 없습니다. 다시 로그인해주세요.');
        return;
      }

      const url = editingUser
        ? getApiUrl(`/api/users/${editingUser.id}`)
        : getApiUrl('/api/users');

      const method = editingUser ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          message.success(`운영자가 성공적으로 ${editingUser ? '수정' : '생성'}되었습니다.`);
          setUserModalVisible(false);
          userForm.resetFields();
          await loadCompanyUsers(); // 목록 새로고침
        } else {
          message.error(data.message || '운영자 저장에 실패했습니다.');
        }
      } else if (response.status === 401) {
        message.error('인증이 만료되었습니다. 다시 로그인해주세요.');
      } else if (response.status === 409) {
        message.error('이미 존재하는 사용자명 또는 이메일입니다.');
      } else {
        const errorData = await response.json().catch(() => ({}));
        console.error('❌ User creation failed:', {
          status: response.status,
          statusText: response.statusText,
          errorData
        }); // 디버그용
        message.error(errorData.error || errorData.message || '운영자 저장 중 오류가 발생했습니다.');
      }
    } catch (error) {
      console.error('운영자 저장 실패:', error);
      if (error instanceof TypeError) {
        message.error('네트워크 연결을 확인해주세요.');
      } else {
        message.error('저장 중 오류가 발생했습니다.');
      }
    } finally {
      setUserLoading(false);
    }
  }, [selectedCompanyId, editingUser, userForm, loadCompanyUsers]);

  const handleDeleteUser = useCallback(async (id: number) => {
    try {
      const token = localStorage.getItem('adminToken');
      if (!token) {
        message.error('인증 토큰이 없습니다. 다시 로그인해주세요.');
        return;
      }

      const response = await fetch(getApiUrl(`/api/users/${id}`), {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          message.success('운영자가 성공적으로 삭제되었습니다.');
          await loadCompanyUsers(); // 목록 새로고침
        } else {
          message.error(data.message || '운영자 삭제에 실패했습니다.');
        }
      } else if (response.status === 401) {
        message.error('인증이 만료되었습니다. 다시 로그인해주세요.');
      } else if (response.status === 404) {
        message.error('삭제할 운영자를 찾을 수 없습니다.');
      } else {
        const errorData = await response.json().catch(() => ({}));
        message.error(errorData.message || '운영자 삭제 중 오류가 발생했습니다.');
      }
    } catch (error) {
      console.error('운영자 삭제 실패:', error);
      if (error instanceof TypeError) {
        message.error('네트워크 연결을 확인해주세요.');
      } else {
        message.error('삭제 중 오류가 발생했습니다.');
      }
    }
  }, [loadCompanyUsers]);

  const handleResetPassword = useCallback(async (userId: number, username: string) => {
    try {
      const token = localStorage.getItem('adminToken');
      if (!token) {
        message.error('인증 토큰이 없습니다. 다시 로그인해주세요.');
        return;
      }

      const response = await fetch(getApiUrl(`/api/users/${userId}/reset-password`), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          Modal.info({
            title: '비밀번호 초기화 완료',
            content: (
              <div>
                <p>사용자 <strong>{username}</strong>의 비밀번호가 초기화되었습니다.</p>
                <p>임시 비밀번호: <strong>{data.data?.temporaryPassword || data.temporaryPassword}</strong></p>
                <p style={{ color: '#ff4d4f', marginTop: 16 }}>
                  ⚠️ 임시 비밀번호를 사용자에게 안전하게 전달하고,<br/>
                  첫 로그인 후 비밀번호를 변경하도록 안내해주세요.
                </p>
              </div>
            ),
            width: 500,
            maskClosable: true
          });
        } else {
          message.error(data.message || '비밀번호 초기화에 실패했습니다.');
        }
      } else if (response.status === 401) {
        message.error('인증이 만료되었습니다. 다시 로그인해주세요.');
      } else if (response.status === 404) {
        message.error('사용자를 찾을 수 없습니다.');
      } else {
        const errorData = await response.json().catch(() => ({}));
        message.error(errorData.message || '비밀번호 초기화 중 오류가 발생했습니다.');
      }
    } catch (error) {
      console.error('비밀번호 초기화 실패:', error);
      if (error instanceof TypeError) {
        message.error('네트워크 연결을 확인해주세요.');
      } else {
        message.error('비밀번호 초기화 중 오류가 발생했습니다.');
      }
    }
  }, []);

  const toggleUserStatus = useCallback(async (id: number, isActive: boolean) => {
    try {
      const token = localStorage.getItem('adminToken');
      if (!token) {
        message.error('인증 토큰이 없습니다. 다시 로그인해주세요.');
        return;
      }

      const response = await fetch(getApiUrl(`/api/users/${id}/status`), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ is_active: isActive })
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          message.success(`운영자가 ${isActive ? '활성화' : '비활성화'}되었습니다.`);
          await loadCompanyUsers(); // 목록 새로고침
        } else {
          message.error(data.message || '상태 변경에 실패했습니다.');
        }
      } else if (response.status === 401) {
        message.error('인증이 만료되었습니다. 다시 로그인해주세요.');
      } else if (response.status === 404) {
        message.error('사용자를 찾을 수 없습니다.');
      } else {
        const errorData = await response.json().catch(() => ({}));
        message.error(errorData.message || '상태 변경 중 오류가 발생했습니다.');
      }
    } catch (error) {
      console.error('상태 변경 실패:', error);
      if (error instanceof TypeError) {
        message.error('네트워크 연결을 확인해주세요.');
      } else {
        message.error('상태 변경 중 오류가 발생했습니다.');
      }
    }
  }, [loadCompanyUsers]);

  // 선택된 운수사가 변경될 때 운영자 목록 로드
  useEffect(() => {
    if (selectedCompanyId && activeTab === 'users') {
      loadCompanyUsers();
    }
  }, [selectedCompanyId, activeTab]);

  const handleAdd = () => {
    setEditingCompany(null);
    form.resetFields();
    // 코드는 서버에서 충돌 없이 자동 생성됨 (소프트삭제된 코드까지 회피).
    // 프론트엔드는 소프트삭제된 코드를 알 수 없어 충돌 코드를 만들 수 있으므로 직접 생성하지 않는다.
    setModalVisible(true);
  };

  const handleEdit = (company: Company) => {
    // 시스템 영상 운수사(ID=0)는 수정 불가
    if (company.id === 0) {
      message.warning('시스템 영상 운수사는 수정할 수 없습니다.');
      return;
    }
    
    setEditingCompany(company);
    form.setFieldsValue({
      name: company.name,
      code: company.code,
      business_number: company.business_number,
      address: company.address,
      phone: company.phone,
      email: company.email,
      contact_person: company.contact_person,
      is_active: company.is_active
    });
    setModalVisible(true);
  };

  // 사업자번호 정규화 (숫자만 추출 후 ###-##-##### 형식으로 변환)
  const normalizeBusinessNumber = (value: string): string => {
    if (!value) return '';
    const numbers = value.replace(/\D/g, '');
    if (numbers.length === 10) {
      return `${numbers.slice(0, 3)}-${numbers.slice(3, 5)}-${numbers.slice(5)}`;
    }
    return value;
  };

  // 전화번호 정규화 (숫자만 추출 후 적절한 형식으로 변환)
  const normalizePhoneNumber = (value: string): string => {
    if (!value) return '';
    const numbers = value.replace(/\D/g, '');
    
    if (numbers.length === 10) {
      // 02-####-#### 또는 ###-###-####
      if (numbers.startsWith('02')) {
        return `${numbers.slice(0, 2)}-${numbers.slice(2, 6)}-${numbers.slice(6)}`;
      } else {
        return `${numbers.slice(0, 3)}-${numbers.slice(3, 6)}-${numbers.slice(6)}`;
      }
    } else if (numbers.length === 11) {
      // ###-####-####
      return `${numbers.slice(0, 3)}-${numbers.slice(3, 7)}-${numbers.slice(7)}`;
    }
    return value;
  };

  const handleSubmit = async (values: any) => {
    try {
      // 데이터 정규화
      const normalizedValues = {
        ...values,
        business_number: normalizeBusinessNumber(values.business_number),
        phone: normalizePhoneNumber(values.phone),
      };
      const token = localStorage.getItem('adminToken');
      const url = editingCompany
        ? getApiUrl(`/api/companies/${editingCompany.id}`)
        : getApiUrl('/api/companies');
      
      const method = editingCompany ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(normalizedValues)
      });

      if (response.ok) {
        message.success(`운수사가 성공적으로 ${editingCompany ? '수정' : '추가'}되었습니다.`);
        setModalVisible(false);
        loadCompanies();
      } else if (response.status === 401) {
        message.error('인증이 만료되었습니다. 다시 로그인해주세요.');
      } else if (response.status === 409) {
        // 코드 중복 등 — 모달을 닫지 않고 사용자가 수정할 수 있도록 유지
        const errorData = await response.json().catch(() => ({}));
        message.error(errorData.error || errorData.message || '이미 존재하는 회사 코드입니다.');
      } else {
        // 서버 오류를 실제로 표시하고 모달을 유지 (가짜 성공 처리 금지)
        const errorData = await response.json().catch(() => ({}));
        message.error(errorData.error || errorData.message || '저장 중 오류가 발생했습니다.');
      }
    } catch (error) {
      console.error('운수사 저장 실패:', error);
      message.error('네트워크 연결을 확인해주세요.');
    }
  };

  const handleDelete = async (id: number) => {
    // 시스템 영상 운수사(ID=0)는 삭제 불가
    if (id === 0) {
      message.warning('시스템 영상 운수사는 삭제할 수 없습니다.');
      return;
    }
    
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(getApiUrl(`/api/companies/${id}`), {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json().catch(() => ({}));
        const tables = data?.data?.deletedTables || {};
        const rowCount = Object.values(tables).reduce((sum: number, n: any) => sum + (Number(n) || 0), 0);
        const files = data?.data?.filesDeleted || 0;
        message.success(
          rowCount || files
            ? `운수사와 연관 데이터가 삭제되었습니다. (연관 행 ${rowCount}건, 파일 ${files}개)`
            : '운수사가 성공적으로 삭제되었습니다.'
        );
        loadCompanies();
      } else if (response.status === 401) {
        message.error('인증이 만료되었습니다. 다시 로그인해주세요.');
      } else {
        const errorData = await response.json().catch(() => ({}));
        message.error(errorData.error || errorData.message || '삭제 중 오류가 발생했습니다.');
      }
    } catch (error) {
      console.error('운수사 삭제 실패:', error);
      message.error('네트워크 연결을 확인해주세요.');
    }
  };

  const toggleStatus = async (id: number, isActive: boolean) => {
    // 시스템 영상 운수사(ID=0)는 상태 변경 불가
    if (id === 0) {
      message.warning('시스템 영상 운수사는 상태를 변경할 수 없습니다.');
      return;
    }
    
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(getApiUrl(`/api/companies/${id}/status`), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ is_active: isActive })
      });

      if (response.ok) {
        message.success(`운수사가 ${isActive ? '활성화' : '비활성화'}되었습니다.`);
        loadCompanies();
      } else if (response.status === 401) {
        message.error('인증이 만료되었습니다. 다시 로그인해주세요.');
      } else {
        const errorData = await response.json().catch(() => ({}));
        message.error(errorData.error || errorData.message || '상태 변경 중 오류가 발생했습니다.');
      }
    } catch (error) {
      console.error('상태 변경 실패:', error);
      message.error('네트워크 연결을 확인해주세요.');
    }
  };

  // 운영자 테이블 컬럼 정의 - useMemo로 최적화
  const userColumns = useMemo(() => [
    {
      title: '사용자 정보',
      key: 'userInfo',
      render: (record: CompanyUser) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Avatar 
            size={40} 
            style={{ 
              backgroundColor: record.role === 'admin' ? '#f56a00' : '#87d068',
              fontSize: 16
            }}
          >
            {record.name?.charAt(0) || record.username?.charAt(0) || 'U'}
          </Avatar>
          <div>
            <div style={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 8 }}>
              {record.name}
              <Tag>
                {record.role === 'admin' ? '관리자' : '운영자'}
              </Tag>
            </div>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {record.username} | {record.email}
            </Text>
            {record.position && (
              <div>
                <Text type="secondary" style={{ fontSize: 11 }}>
                  {record.position}
                </Text>
              </div>
            )}
          </div>
        </div>
      )
    },
    {
      title: '연락처',
      key: 'contact',
      responsive: ['md'] as any,
      render: (record: CompanyUser) => (
        <div>
          {record.phone && (
            <div><PhoneOutlined /> {record.phone}</div>
          )}
          {record.last_login && (
            <div style={{ color: '#999', fontSize: 12 }}>
              마지막 접속: {new Date(record.last_login).toLocaleString('ko-KR')}
            </div>
          )}
        </div>
      )
    },
    {
      title: '상태',
      dataIndex: 'is_active',
      key: 'status',
      render: (isActive: boolean, record: CompanyUser) => (
        <div>
          <Switch
            checked={isActive}
            onChange={(checked) => toggleUserStatus(record.id, checked)}
            checkedChildren="활성"
            unCheckedChildren="비활성"
            size="small"
          />
          <div style={{ marginTop: 4 }}>
            <Badge 
              status={isActive ? 'success' : 'default'} 
              text={
                <Text style={{ fontSize: 11 }}>
                  {new Date(record.created_at).toLocaleDateString('ko-KR')}
                </Text>
              } 
            />
          </div>
        </div>
      )
    },
    {
      title: '작업',
      key: 'actions',
      render: (record: CompanyUser) => (
        <Space direction={isMobile ? 'vertical' : 'horizontal'} size="small">
          <Tooltip title="정보 수정">
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={() => handleEditUser(record)}
              size="small"
            />
          </Tooltip>
          <Tooltip title="비밀번호 초기화">
            <Popconfirm
              title="비밀번호 초기화"
              description="이 사용자의 비밀번호를 초기화하시겠습니까?"
              onConfirm={() => handleResetPassword(record.id, record.username)}
              okText="초기화"
              cancelText="취소"
            >
              <Button
                type="text"
                icon={<LockOutlined />}
                size="small"
              />
            </Popconfirm>
          </Tooltip>
          <Tooltip title="계정 삭제">
            <Popconfirm
              title="운영자 삭제"
              description="이 운영자를 정말 삭제하시겠습니까? 삭제된 데이터는 복구할 수 없습니다."
              onConfirm={() => handleDeleteUser(record.id)}
              okText="삭제"
              cancelText="취소"
            >
              <Button
                type="text"
                danger
                icon={<DeleteOutlined />}
                size="small"
              />
            </Popconfirm>
          </Tooltip>
        </Space>
      )
    }
  ], [handleEditUser, handleResetPassword, handleDeleteUser, toggleUserStatus, isMobile]);

  // 운수사 테이블 컬럼 정의 - useMemo로 최적화
  const columns = useMemo(() => [
    {
      title: '운수사명',
      dataIndex: 'name',
      key: 'name',
      render: (text: string, record: Company) => (
        <div>
          <div style={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '8px' }}>
            {text}
            {record.id === 0 && (
              <Tag>시스템</Tag>
            )}
            <Button
              type="text"
              size="small"
              onClick={() => {
                setSelectedCompanyId(record.id);
                setActiveTab('users');
              }}
              style={{ padding: 0, height: 'auto', marginLeft: 8 }}
            >
              <TeamOutlined style={{ color: '#1890ff' }} />
            </Button>
          </div>
          <Text type="secondary" style={{ fontSize: 12 }}>
            코드: {record.code}
          </Text>
        </div>
      )
    },
    {
      title: '연락처',
      key: 'contact',
      responsive: ['md'] as any,
      render: (record: Company) => (
        <div>
          {record.contact_person && (
            <div><UserOutlined /> {record.contact_person}</div>
          )}
          {record.phone && (
            <div><PhoneOutlined /> {record.phone}</div>
          )}
          {record.email && (
            <div><MailOutlined /> {record.email}</div>
          )}
        </div>
      )
    },
    {
      title: '통계',
      key: 'stats',
      responsive: ['lg'] as any,
      render: (record: Company) => (
        <Space direction="vertical" size="small">
          <Text>사용자: {record.user_count || 0}명</Text>
          <Text>노선: {record.route_count || 0}개</Text>
          <Text>단말기: {record.device_count || 0}대</Text>
        </Space>
      )
    },
    {
      title: '상태',
      dataIndex: 'is_active',
      key: 'status',
      render: (isActive: boolean, record: Company) => (
        <Switch
          checked={isActive}
          onChange={(checked) => toggleStatus(record.id, checked)}
          disabled={record.id === 0}  // 시스템 영상 운수사는 상태 변경 불가
          checkedChildren="활성"
          unCheckedChildren="비활성"
        />
      )
    },
    {
      title: '작업',
      key: 'actions',
      width: 120,
      align: 'center' as const,
      render: (record: Company) => (
        <Space direction="vertical" size="small" style={{ width: '100%' }}>
          <Tooltip title="운영자 관리">
            <Button
              type="text"
              icon={<TeamOutlined />}
              onClick={() => {
                setSelectedCompanyId(record.id);
                setActiveTab('users');
              }}
              size="small"
              block
            >
              운영자 관리
            </Button>
          </Tooltip>
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
            disabled={record.id === 0}  // 시스템 영상 운수사는 수정 불가
            size="small"
            block
          >
            수정
          </Button>
          <Popconfirm
            title="운수사 영구 삭제"
            description={
              <div style={{ maxWidth: 280 }}>
                <strong>{record.name}</strong>와(과) 연결된 모든 데이터
                (노선·미디어 파일·날씨·뉴스·앱설정·단말기·타겟광고·운영자)를
                <strong style={{ color: '#ff4d4f' }}> 영구 삭제</strong>합니다.
                <br />이 작업은 되돌릴 수 없습니다.
              </div>
            }
            onConfirm={() => handleDelete(record.id)}
            okText="영구 삭제"
            okButtonProps={{ danger: true }}
            cancelText="취소"
            disabled={record.id === 0}  // 시스템 영상 운수사는 삭제 불가
          >
            <Button
              type="text"
              danger
              icon={<DeleteOutlined />}
              disabled={record.id === 0}  // 시스템 영상 운수사는 삭제 불가
              size="small"
              block
            >
              삭제
            </Button>
          </Popconfirm>
        </Space>
      )
    }
  ], [handleEdit, toggleStatus, handleDelete, setSelectedCompanyId, setActiveTab, isMobile]);

  if (user?.role !== 'admin') {
    return (
      <div style={{ padding: isMobile ? 12 : 24, textAlign: 'center' }}>
        <Title level={3}>접근 권한이 없습니다</Title>
        <Text>관리자만 운수사 관리 기능을 사용할 수 있습니다.</Text>
      </div>
    );
  }

  return (
    <PageLayout
      title="운수사 관리"
      subtitle="운수사 등록, 수정, 삭제 및 운영자 계정 관리"
      actions={[
        {
          key: 'refresh',
          label: '새로고침',
          icon: <ReloadOutlined />,
          onClick: () => {
            loadCompanies();
            if (selectedCompanyId) {
              loadCompanyUsers();
            }
          }
        }
      ]}
    >
      
      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        items={[
          {
            key: 'companies',
            label: (
              <span>
                <BankOutlined /> 운수사 목록
              </span>
            ),
            children: renderCompaniesTab()
          },
          {
            key: 'users',
            label: (
              <span>
                <TeamOutlined /> 운영자 관리
              </span>
            ),
            children: renderUsersTab()
          }
        ]}
      />

      {/* 추가/수정 모달 */}
      <Modal
        title={editingCompany ? '운수사 수정' : '운수사 추가'}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={null}
        width={isMobile ? '95%' : 600}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="name"
                label="운수사명"
                rules={[{ required: true, message: '운수사명을 입력해주세요' }]}
              >
                <Input placeholder="운수사명" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="code"
                label="운수사 코드"
                rules={editingCompany ? [{ required: true, message: '운수사 코드를 입력해주세요' }] : []}
              >
                <Input
                  placeholder={editingCompany ? '운수사 코드' : '서버에서 자동 생성됩니다'}
                  disabled={!editingCompany}
                  style={{ backgroundColor: editingCompany ? undefined : '#f5f5f5' }}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="business_number"
                label="사업자번호"
                rules={[
                  {
                    pattern: /^\d{3}-?\d{2}-?\d{5}$|^\d{10}$/,
                    message: '올바른 사업자번호를 입력해주세요 (예: 123-45-67890 또는 1234567890)'
                  }
                ]}
              >
                <Input 
                  placeholder="123-45-67890" 
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, '');
                    if (value.length <= 10) {
                      form.setFieldsValue({ 
                        business_number: value.length === 10 
                          ? `${value.slice(0,3)}-${value.slice(3,5)}-${value.slice(5)}`
                          : value
                      });
                    }
                  }}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="contact_person"
                label="담당자"
              >
                <Input placeholder="담당자명" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="address"
            label="주소"
          >
            <TextArea rows={2} placeholder="운수사 주소" />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="phone"
                label="전화번호"
                rules={[
                  {
                    pattern: /^(\d{2,3}-?\d{3,4}-?\d{4})$/,
                    message: '올바른 전화번호를 입력해주세요 (예: 02-123-4567, 010-1234-5678)'
                  }
                ]}
              >
                <Input 
                  placeholder="02-123-4567 또는 010-1234-5678" 
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, '');
                    let formatted = '';
                    
                    if (value.length <= 11) {
                      if (value.startsWith('02') && value.length <= 10) {
                        // 서울 지역번호 02-####-####
                        if (value.length > 2) formatted += value.slice(0, 2) + '-';
                        if (value.length > 6) formatted += value.slice(2, 6) + '-';
                        if (value.length > 6) formatted += value.slice(6);
                        else if (value.length > 2) formatted += value.slice(2);
                      } else if (value.length === 10) {
                        // 일반 지역번호 ###-###-####
                        formatted = `${value.slice(0, 3)}-${value.slice(3, 6)}-${value.slice(6)}`;
                      } else if (value.length === 11) {
                        // 휴대폰 ###-####-####
                        formatted = `${value.slice(0, 3)}-${value.slice(3, 7)}-${value.slice(7)}`;
                      } else {
                        formatted = value;
                      }
                      
                      form.setFieldsValue({ phone: formatted });
                    }
                  }}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="email"
                label="이메일"
                rules={[
                  {
                    type: 'email',
                    message: '올바른 이메일 주소를 입력해주세요'
                  }
                ]}
              >
                <Input placeholder="contact@company.com" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="is_active"
            label="상태"
            valuePropName="checked"
            initialValue={true}
          >
            <Switch
              checkedChildren="활성"
              unCheckedChildren="비활성"
            />
          </Form.Item>

          <div style={{ textAlign: 'right', marginTop: 24 }}>
            <Space>
              <Button onClick={() => setModalVisible(false)}>
                취소
              </Button>
              <Button type="primary" htmlType="submit">
                {editingCompany ? '수정' : '추가'}
              </Button>
            </Space>
          </div>
        </Form>
      </Modal>

      {/* 운영자 추가/수정 모달 */}
      <Modal
        title={editingUser ? '운영자 수정' : '운영자 추가'}
        open={userModalVisible}
        onCancel={() => setUserModalVisible(false)}
        footer={null}
        width={isMobile ? '95%' : 600}
      >
        <Form
          form={userForm}
          layout="vertical"
          onFinish={handleUserSubmit}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="username"
                label="사용자명"
                rules={[
                  { required: true, message: '사용자명을 입력해주세요' },
                  { min: 3, message: '사용자명은 3자 이상이어야 합니다' },
                  { pattern: /^[a-zA-Z0-9_]+$/, message: '사용자명은 영문, 숫자, 언더바만 사용 가능합니다' }
                ]}
              >
                <Input 
                  placeholder="영문, 숫자, 언더바만 사용" 
                  disabled={!!editingUser}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="name"
                label="실명"
                rules={[{ required: true, message: '실명을 입력해주세요' }]}
              >
                <Input placeholder="홍길동" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="email"
                label="이메일"
                rules={[
                  { required: true, message: '이메일을 입력해주세요' },
                  { type: 'email', message: '올바른 이메일 주소를 입력해주세요' }
                ]}
              >
                <Input placeholder="user@company.com" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="role"
                label="권한"
                rules={[{ required: true, message: '권한을 선택해주세요' }]}
              >
                <Select placeholder="권한 선택">
                  <Select.Option value="operator">운영자</Select.Option>
                  {user?.role === 'admin' && (
                    <Select.Option value="admin">관리자</Select.Option>
                  )}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="phone"
                label={<span>연락처 <span style={{ color: 'red' }}>*</span></span>}
                rules={[
                  { required: true, message: '연락처를 입력해주세요' },
                  {
                    pattern: /^(\d{2,3}-?\d{3,4}-?\d{4})$/,
                    message: '올바른 전화번호를 입력해주세요 (예: 010-1234-5678)'
                  }
                ]}
              >
                <Input placeholder="010-1234-5678" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="position"
                label="직급"
              >
                <Input placeholder="운영팀장" />
              </Form.Item>
            </Col>
          </Row>

          {!editingUser && (
            <Form.Item
              name="password"
              label="비밀번호"
              rules={[
                { required: true, message: '비밀번호를 입력해주세요' },
                { min: 8, message: '비밀번호는 8자 이상이어야 합니다' },
                {
                  pattern: /^(?=.*[A-Z])(?=.*[a-z])(?=.*\d).{8,}$/,
                  message: '비밀번호는 대문자, 소문자, 숫자를 각각 1개 이상 포함하여 8자 이상이어야 합니다'
                }
              ]}
              hasFeedback
            >
              <Input.Password
                placeholder="대문자+소문자+숫자 조합 8자 이상"
                visibilityToggle
              />
            </Form.Item>
          )}

          <Form.Item
            name="is_active"
            label="상태"
            valuePropName="checked"
            initialValue={true}
          >
            <Switch
              checkedChildren="활성"
              unCheckedChildren="비활성"
            />
          </Form.Item>

          <div style={{ textAlign: 'right', marginTop: 24 }}>
            <Space>
              <Button onClick={() => setUserModalVisible(false)}>
                취소
              </Button>
              <Button type="primary" htmlType="submit" loading={userLoading}>
                {editingUser ? '수정' : '생성'}
              </Button>
            </Space>
          </div>
        </Form>
      </Modal>

    </PageLayout>
  );

  function renderCompaniesTab() {
    return (
      <div>
        {/* 통계 카드 */}
        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          <Col xs={12} sm={6}>
            <Card>
              <Statistic
                title="총 운수사"
                value={stats.totalCompanies}
                prefix={<BankOutlined />}
              />
            </Card>
          </Col>
          <Col xs={12} sm={6}>
            <Card>
              <Statistic
                title="활성 운수사"
                value={stats.activeCompanies}
                valueStyle={{ color: '#3f8600' }}
              />
            </Card>
          </Col>
          <Col xs={12} sm={6}>
            <Card>
              <Statistic
                title="총 사용자"
                value={stats.totalUsers}
                prefix={<UserOutlined />}
              />
            </Card>
          </Col>
          <Col xs={12} sm={6}>
            <Card>
              <Statistic
                title="총 노선"
                value={stats.totalRoutes}
              />
            </Card>
          </Col>
        </Row>

        {/* 검색 및 액션 버튼 */}
        <div style={{
          marginBottom: 16,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 16
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Input
              placeholder="운수사명, 코드, 담당자로 검색..."
              prefix={<SearchOutlined />}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{ width: isMobile ? 200 : 300 }}
              allowClear
              suffix={
                searchTerm && (
                  <Button
                    type="text"
                    size="small"
                    icon={<ClearOutlined />}
                    onClick={() => setSearchTerm('')}
                  />
                )
              }
            />
            {searchTerm && (
              <Text type="secondary" style={{ fontSize: 12 }}>
                {filteredCompanies.length}개 결과
              </Text>
            )}
          </div>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleAdd}
            size={isMobile ? 'middle' : 'large'}
          >
            운수사 추가
          </Button>
        </div>

        {/* 테이블 */}
        <Card>
          {filteredCompanies.length === 0 && searchTerm ? (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={
                <span>
                  "<strong>{searchTerm}</strong>"에 대한 검색 결과가 없습니다.
                </span>
              }
            />
          ) : (
            <Table
              columns={columns}
              dataSource={filteredCompanies}
              rowKey="id"
              loading={loading}
              pagination={{
                total: filteredCompanies.length,
                pageSize: isMobile ? 5 : 10,
                showSizeChanger: !isMobile,
                showQuickJumper: !isMobile,
                showTotal: (total, range) =>
                  `${range[0]}-${range[1]} / 총 ${total}개`
              }}
              scroll={{ x: isMobile ? 800 : undefined }}
              locale={{
                emptyText: (
                  <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description="등록된 운수사가 없습니다."
                  />
                )
              }}
            />
          )}
        </Card>
      </div>
    );
  }
  
  function renderUsersTab() {
    return (
      <div>
        {/* 운수사 선택 */}
        <Card style={{ marginBottom: 16 }}>
          <Row gutter={16} align="middle">
            <Col flex="auto">
              <Select
                value={selectedCompanyId}
                onChange={setSelectedCompanyId}
                placeholder="운영자를 관리할 운수사를 선택하세요"
                style={{ width: '100%' }}
                size="large"
                showSearch
                filterOption={(input, option) =>
                  (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                }
                options={companySelectOptions}
              />
            </Col>
            <Col>
              <Button
                icon={<ReloadOutlined />}
                onClick={loadCompanyUsers}
                disabled={!selectedCompanyId}
              >
                새로고침
              </Button>
            </Col>
          </Row>
        </Card>

        {selectedCompanyId && (
          <>
            <Alert
              message={
                <span>
                  <strong>{companies.find(c => c.id === selectedCompanyId)?.name}</strong>의 운영자를 관리합니다.
                </span>
              }
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
              action={
                <Button
                  size="small"
                  type="text"
                  onClick={() => {
                    setSelectedCompanyId(null);
                    setSearchTerm('');
                  }}
                >
                  다른 운수사 선택
                </Button>
              }
            />
            {/* 검색 및 운영자 액션 버튼 */}
            <div style={{
              marginBottom: 16,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 16
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Input
                  placeholder="이름, 사용자명, 이메일로 검색..."
                  prefix={<SearchOutlined />}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{ width: isMobile ? 200 : 300 }}
                  allowClear
                />
                {searchTerm && (
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {Array.isArray(filteredUsers) ? filteredUsers.length : 0}개 결과
                  </Text>
                )}
              </div>
              <Button
                type="primary"
                icon={<UserAddOutlined />}
                onClick={handleAddUser}
                size={isMobile ? 'middle' : 'large'}
              >
                운영자 추가
              </Button>
            </div>

            {/* 운영자 목록 테이블 */}
            <Card>
              {Array.isArray(filteredUsers) && filteredUsers.length === 0 && searchTerm ? (
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description={
                    <span>
                      "<strong>{searchTerm}</strong>"에 대한 검색 결과가 없습니다.
                    </span>
                  }
                />
              ) : (
                <Table
                  columns={userColumns}
                  dataSource={Array.isArray(filteredUsers) ? filteredUsers : []}
                  rowKey="id"
                  loading={userLoading}
                  pagination={{
                    total: Array.isArray(filteredUsers) ? filteredUsers.length : 0,
                    pageSize: isMobile ? 5 : 10,
                    showSizeChanger: !isMobile,
                    showQuickJumper: !isMobile,
                    showTotal: (total, range) =>
                      `${range[0]}-${range[1]} / 총 ${total}개`
                  }}
                  scroll={{ x: isMobile ? 800 : undefined }}
                  locale={{
                    emptyText: (
                      <Empty
                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                        description="등록된 운영자가 없습니다."
                      />
                    )
                  }}
                />
              )}
            </Card>
          </>
        )}
      </div>
    );
  }
});

CompanyManagement.displayName = 'CompanyManagement';

export default CompanyManagement;